import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Save, Loader2, Trash2, Wrench, Mail, Phone, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { PhotoGallery } from '@/components/PhotoGallery';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';

export default function SubcontractorDetailPage() {
  const { subcontractorId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tf = (fr, en) => language === 'fr' ? fr : en;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', email: '', specialty: 'excavation', status: 'actif', notes: '' });
  const [createdAt, setCreatedAt] = useState('');
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [mobilePassword, setMobilePassword] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);

  const handleSetPassword = async () => {
    if (mobilePassword.length < 6) {
      toast.error(tf('Le mot de passe doit avoir au moins 6 caracteres', 'Password must be at least 6 characters'));
      return;
    }
    if (!form.email) {
      toast.error(tf('Un courriel est requis pour l\'acces mobile', 'An email is required for mobile access'));
      return;
    }
    setSettingPassword(true);
    try {
      await api.post(`/mobile/subcontractors/${subcontractorId}/set-password`, { password: mobilePassword });
      toast.success(tf('Acces mobile active!', 'Mobile access activated!'));
      setShowPasswordDialog(false);
      setMobilePassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || tf('Erreur', 'Error'));
    } finally { setSettingPassword(false); }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/subcontractors/${subcontractorId}`);
        setForm({
          first_name: data.first_name || '', last_name: data.last_name || '',
          phone: data.phone || '', email: data.email || '',
          specialty: data.specialty || 'autre', status: data.status || 'actif',
          notes: data.notes || '',
        });
        setCreatedAt(data.created_at || '');
      } catch {
        toast.error(tf('Sous-traitant introuvable', 'Subcontractor not found'));
        navigate('/subcontractors');
      } finally { setLoading(false); }
    };
    load();
  }, [subcontractorId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/subcontractors/${subcontractorId}`, form);
      toast.success(tf('Sous-traitant mis a jour', 'Subcontractor updated'));
    } catch (e) { toast.error(e.response?.data?.detail || tf('Erreur', 'Error')); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(tf('Supprimer ce sous-traitant?', 'Delete this subcontractor?'))) return;
    try {
      await api.delete(`/subcontractors/${subcontractorId}`);
      toast.success(tf('Sous-traitant supprime', 'Subcontractor deleted'));
      navigate('/subcontractors');
    } catch { toast.error(tf('Erreur', 'Error')); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="subcontractor-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/subcontractors')} data-testid="back-to-subcontractors">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-black tracking-tight">
              {form.first_name} {form.last_name}
            </h1>
            {createdAt && (
              <p className="text-sm text-muted-foreground">
                {tf('Ajoute le', 'Added')} {new Date(createdAt).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA')}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="default" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setShowPasswordDialog(true)} data-testid="set-sub-mobile-password-btn">
            <Smartphone className="h-4 w-4 mr-2" />{tf('Acces mobile', 'Mobile access')}
          </Button>
          <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="delete-subcontractor-btn">
            <Trash2 className="h-4 w-4 mr-2" />{tf('Supprimer', 'Delete')}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Wrench className="h-5 w-5" />
            {tf('Informations du sous-traitant', 'Subcontractor information')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{tf('Prenom', 'First name')}</Label>
              <Input value={form.first_name} onChange={e => setForm(f => ({...f, first_name: e.target.value}))} data-testid="edit-sub-firstname" />
            </div>
            <div className="space-y-1.5">
              <Label>{tf('Nom', 'Last name')}</Label>
              <Input value={form.last_name} onChange={e => setForm(f => ({...f, last_name: e.target.value}))} data-testid="edit-sub-lastname" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {tf('Telephone', 'Phone')}</Label>
              <Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} data-testid="edit-sub-phone" />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Courriel</Label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} data-testid="edit-sub-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{tf('Specialite', 'Specialty')}</Label>
              <Select value={form.specialty} onValueChange={v => setForm(f => ({...f, specialty: v}))}>
                <SelectTrigger data-testid="edit-sub-specialty"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="excavation">Excavation</SelectItem>
                  <SelectItem value="electricite">{tf('Electricite', 'Electrical')}</SelectItem>
                  <SelectItem value="plomberie">{tf('Plomberie', 'Plumbing')}</SelectItem>
                  <SelectItem value="beton">{tf('Beton', 'Concrete')}</SelectItem>
                  <SelectItem value="paysagement">{tf('Paysagement', 'Landscaping')}</SelectItem>
                  <SelectItem value="autre">{tf('Autre', 'Other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{tf('Statut', 'Status')}</Label>
              <Select value={form.status} onValueChange={v => setForm(f => ({...f, status: v}))}>
                <SelectTrigger data-testid="edit-sub-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="actif">{tf('Actif', 'Active')}</SelectItem>
                  <SelectItem value="inactif">{tf('Inactif', 'Inactive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={4} data-testid="edit-sub-notes" />
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="save-subcontractor-edit-btn">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {tf('Sauvegarder', 'Save')}
          </Button>
        </CardContent>
      </Card>

      <PhotoGallery entityType="subcontractor" entityId={subcontractorId} />

      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Smartphone className="h-5 w-5" /> {tf('Acces mobile', 'Mobile access')}</DialogTitle>
            <DialogDescription>
              {tf(
                `Definissez un mot de passe pour que ${form.first_name} puisse se connecter a l'application mobile avec son courriel.`,
                `Set a password so ${form.first_name} can log into the mobile app with their email.`
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{tf('Courriel', 'Email')}</label>
              <Input value={form.email} disabled className="bg-muted" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{tf('Mot de passe mobile', 'Mobile password')}</label>
              <Input
                type="password" value={mobilePassword}
                onChange={e => setMobilePassword(e.target.value)}
                placeholder={tf('Minimum 6 caracteres', 'Minimum 6 characters')}
                data-testid="sub-mobile-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>{tf('Annuler', 'Cancel')}</Button>
            <Button onClick={handleSetPassword} disabled={settingPassword || !form.email} data-testid="confirm-set-sub-password-btn">
              {settingPassword && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {tf('Activer', 'Activate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
